/**
 * Auth Store - Zustand
 * TMC Studio - Authentication state management
 * 
 * SIMPLIFIED VERSION - let Supabase handle OAuth automatically
 */

import { logger } from '../lib/logger';
import { accessFromUser } from '../lib/entitlements';
// DEV-ONLY: see ../lib/devCloud.ts
import { setDevCloudUser, clearDevCloudData, clearAllDevCloudData, isDevCloudActive } from '../lib/devCloud';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User as SupabaseAuthUser } from '@supabase/supabase-js';
import { track, EVENTS } from '../lib/analytics';
import { openOAuthPopup, waitForOAuthPopupCode, AUTH_POPUP_CALLBACK_PATH } from '../auth/oauthWebPopup';
import { getDesktopAuthRedirectTo, waitForDesktopOAuthCode } from '../auth/oauthDesktopBridge';
import { resolveGoogleOAuthSurface } from '../auth/oauthSurface';
import { idleAuthFlow, type AuthFlowState } from '../auth/authFlow';
import {
  isSupabaseEnabled,
  getCurrentUser,
  signIn as supabaseSignIn,
  signUp as supabaseSignUp,
  signOut as supabaseSignOut,
  signInWithGoogle as supabaseSignInWithGoogle,
  onAuthStateChange,
  getPreferences,
  updatePreferences,
  resetPasswordForEmail as supabaseResetPasswordForEmail,
  resendConfirmationEmail as supabaseResendConfirmation,
  supabase,
  type User,
} from '../lib/supabase';

interface AuthState {
  // State
  user: User | null;
  isLoading: boolean;
  isOAuthInProgress: boolean;
  authFlow: AuthFlowState;
  isInitialized: boolean;
  error: string | null;
  /** DEV-ONLY: true when the current session was created via devLogin()
   *  (test login button), not via real Supabase auth. */
  isMockUser: boolean;

  // Actions
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  sendResetLink: (email: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  clearError: () => void;
  /** DEV-ONLY: instantly "log in" as a fake user with the given plan,
   *  without going through Google/Supabase. Used by the "Test login"
   *  buttons (only rendered when import.meta.env.DEV is true). */
  devLogin: (tier: 'guest' | 'free' | 'pro' | 'team') => void;
  /** DEV-ONLY: clear a mock session created via devLogin(). */
  devLogout: () => void;
  /** DEV-ONLY: wipe this mock user's devCloud projects/folders and reset the board. */
  devClearData: () => void;
  /** DEV-ONLY: wipe devCloud projects/folders for ALL test tiers (free/pro/team).
   *  Useful for long test sessions where other tiers' data accumulates. */
  devClearAllTiers: () => void;

  // Computed
  isAuthenticated: boolean;
  isPro: boolean;
  isTeam: boolean;
  /** Club Premium team ID if user is a member of a team */
  teamId: string | null;
}

const AUTH_CALLBACK_PARAMS = ['code', 'state', 'error', 'error_code', 'error_description'];

function hasAuthCallbackInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return AUTH_CALLBACK_PARAMS.some((key) => params.has(key)) ||
    window.location.hash.includes('access_token') ||
    window.location.hash.includes('error');
}

function hasAuthErrorInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('error') || params.has('error_code') || params.has('error_description') ||
    window.location.hash.includes('error');
}

function cleanAuthCallbackUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;

  AUTH_CALLBACK_PARAMS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (url.hash.includes('access_token') || url.hash.includes('error')) {
    url.hash = '';
    changed = true;
  }

  if (changed) {
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(null, '', next || '/board');
  }
}

let authUnsubscribe: (() => void) | null = null;

/**
 * B1.1/4 DRY: apply cloud preferences to the UI store.
 * Loads ALL fields from cloudPrefs (theme, grid, snap, arrowDefaults, etc.)
 * and merges them into useUIStore. Must be called from any auth path that
 * establishes a session (init, restore, Google popup, fallback).
 */
async function applyCloudPreferences(cloudPrefs?: any): Promise<void> {
  const { useUIStore } = await import('./useUIStore');
  
  if (!cloudPrefs || Object.keys(cloudPrefs).length === 0) {
    // B1.5: first login — push local preferences to cloud
    const local = useUIStore.getState();
    updatePreferences({
      theme: local.theme,
      themeMode: local.themeMode,
      gridVisible: local.gridVisible,
      snapEnabled: local.snapEnabled,
      gridSize: local.gridSize,
      defaultArrowType: local.defaultArrowType,
      stepDuration: local.stepDuration,
      arrowDefaults: {
        strokeWidth: local.arrowDefaults.strokeWidth,
        color: local.arrowDefaults.color,
        startHead: local.arrowDefaults.startHead,
        endHead: local.arrowDefaults.endHead,
      },
      zoneDefaults: {
        borderStyle: local.zoneDefaults.borderStyle,
        borderWidth: local.zoneDefaults.borderWidth,
        borderColor: local.zoneDefaults.borderColor,
        showCorners: local.zoneDefaults.showCorners,
        fillColor: local.zoneDefaults.fillColor,
        opacity: local.zoneDefaults.opacity,
      },
      ballDefaults: local.ballDefaults,
      textDefaults: local.textDefaults,
      equipmentDefaults: local.equipmentDefaults,
      squadBenchVisible: local.squadBenchVisible,
      shortcutOverrides: local.shortcutOverrides,
      coachingProfile: local.coachingProfile,
      bottomBar: { height: local.bottomBarHeight, collapsed: local.bottomBarCollapsed },
      inspector: { width: local.inspectorWidth },
    }).catch(() => {});
    return;
  }

  // Load all fields from cloud (cloud takes precedence over local)
  if (cloudPrefs.themeMode) {
    useUIStore.getState().setThemeMode(cloudPrefs.themeMode);
  } else if (cloudPrefs.theme) {
    useUIStore.getState().setTheme(cloudPrefs.theme);
  }
  if (cloudPrefs.gridVisible !== undefined) useUIStore.setState({ gridVisible: cloudPrefs.gridVisible });
  if (cloudPrefs.snapEnabled !== undefined) useUIStore.setState({ snapEnabled: cloudPrefs.snapEnabled });
  if (cloudPrefs.gridSize !== undefined) useUIStore.setState({ gridSize: cloudPrefs.gridSize });
  if (cloudPrefs.defaultArrowType) useUIStore.setState({ defaultArrowType: cloudPrefs.defaultArrowType as any });
  if (cloudPrefs.stepDuration !== undefined) useUIStore.setState({ stepDuration: cloudPrefs.stepDuration });
  if (cloudPrefs.arrowDefaults) {
    const current = useUIStore.getState().arrowDefaults;
    useUIStore.setState({
      arrowDefaults: {
        ...current,
        ...cloudPrefs.arrowDefaults,
        strokeWidth: { ...current.strokeWidth, ...(cloudPrefs.arrowDefaults.strokeWidth ?? {}) },
        color: { ...(current.color ?? {}), ...(cloudPrefs.arrowDefaults.color ?? {}) },
      } as any,
    });
  }
  if (cloudPrefs.zoneDefaults) {
    useUIStore.setState({
      zoneDefaults: { ...useUIStore.getState().zoneDefaults, ...cloudPrefs.zoneDefaults } as any,
    });
  }
  if (cloudPrefs.ballDefaults) useUIStore.setState({ ballDefaults: { ...useUIStore.getState().ballDefaults, ...cloudPrefs.ballDefaults } });
  if (cloudPrefs.textDefaults) useUIStore.setState({ textDefaults: { ...useUIStore.getState().textDefaults, ...cloudPrefs.textDefaults } });
  if (cloudPrefs.equipmentDefaults) useUIStore.setState({ equipmentDefaults: { ...useUIStore.getState().equipmentDefaults, ...cloudPrefs.equipmentDefaults } });
  if (cloudPrefs.squadBenchVisible !== undefined) useUIStore.setState({ squadBenchVisible: cloudPrefs.squadBenchVisible });
  if (cloudPrefs.shortcutOverrides) useUIStore.setState({ shortcutOverrides: cloudPrefs.shortcutOverrides });
  if (cloudPrefs.coachingProfile) {
    const currentProfile = useUIStore.getState().coachingProfile;
    useUIStore.setState({
      coachingProfile: {
        ...currentProfile,
        ...cloudPrefs.coachingProfile,
        staff: cloudPrefs.coachingProfile.staff ?? currentProfile.staff,
        squad: cloudPrefs.coachingProfile.squad ?? currentProfile.squad ?? [],
        positionGroups: cloudPrefs.coachingProfile.positionGroups ?? currentProfile.positionGroups ?? [],
        sessionDefaults: {
          dateOffsetDays: 1,
          nameTemplate: '',
          venue: '',
          startTime: '',
          microcycleDay: '',
          ...currentProfile.sessionDefaults,
          ...cloudPrefs.coachingProfile.sessionDefaults,
        },
      },
    });
  }
  if (cloudPrefs.bottomBar) {
    useUIStore.setState({
      bottomBarHeight: cloudPrefs.bottomBar.height,
      bottomBarCollapsed: cloudPrefs.bottomBar.collapsed ?? false,
    });
  }
  if (cloudPrefs.inspector) {
    useUIStore.setState({ inspectorWidth: cloudPrefs.inspector.width });
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      // Initial state
      user: null,
      isLoading: false,
      isOAuthInProgress: false,
      authFlow: idleAuthFlow,
      isInitialized: false,
      error: null,
      isMockUser: false,
      isAuthenticated: false,
      isPro: false,
      isTeam: false,
      teamId: null,

      // Initialize auth - call on app startup (EVENT-BASED, NON-BLOCKING)
      initialize: async () => {
        logger.debug('[Auth] Initialize started (event-based)');

        // DEV-ONLY: if a mock (test login) session is active, skip Supabase
        // entirely so the real auth listener doesn't override/clear it.
        if (_get().isMockUser) {
          // Guard against a desynced dev session: the mock user lives in this
          // (persisted) store, but the devCloud "is logged in" flag lives in a
          // separate localStorage key. If that key was cleared independently
          // (e.g. by hand in DevTools) and there is no real Supabase to fall
          // back to, sync would silently break. Reconcile by clearing the
          // stale mock session instead of showing a logged-in user with no cloud.
          if (!isDevCloudActive() && !supabase) {
            logger.warn('[Auth] Mock session present but devCloud inactive - clearing stale mock session');
            set({
              user: null,
              isAuthenticated: false,
              isPro: false,
              isTeam: false,
              teamId: null,
              isMockUser: false,
              isInitialized: true,
              isLoading: false,
              isOAuthInProgress: false,
              authFlow: idleAuthFlow,
            });
            return;
          }
          logger.debug('[Auth] Mock dev session active - skipping Supabase init');
          set({ isInitialized: true, isLoading: false, isOAuthInProgress: false, authFlow: idleAuthFlow });
          return;
        }

        if (!isSupabaseEnabled() || !supabase) {
          logger.debug('[Auth] Supabase disabled - using offline mode');
          set({ isInitialized: true, isLoading: false, isOAuthInProgress: false, authFlow: idleAuthFlow });
          return;
        }

        // ✅ UI starts IMMEDIATELY - no blocking
        set({ isInitialized: true, isLoading: false });

        try {
          const hasAuthCallback = hasAuthCallbackInUrl();
          
          if (hasAuthCallback) {
            logger.debug('[Auth] OAuth callback detected - waiting for session before URL cleanup');
            if (hasAuthErrorInUrl()) {
              cleanAuthCallbackUrl();
            }
          }

          // ✅ Setup listener for future auth changes
          if (!authUnsubscribe) {
            logger.debug('[Auth] Setting up auth listener...');
            authUnsubscribe = onAuthStateChange(async (user) => {
              logger.debug('[Auth] State changed - user:', user?.email ?? 'none');
              
              set({
                user,
                isAuthenticated: !!user,
                ...accessFromUser(user),
              });

              // Load preferences from cloud if user is authenticated
              if (user) {
                logger.debug('[Auth] Loading preferences from cloud...');
                try {
                  const cloudPrefs = await getPreferences();
                  await applyCloudPreferences(cloudPrefs);
                  logger.debug('[Auth] Preferences loaded from cloud');
                } catch (error) {
                  logger.error('[Auth] Failed to load preferences:', error);
                }

                // Prefetch projects/folders so project-limit counts are correct
                // immediately after sign-in (before the drawer is ever opened).
                try {
                  const { useBoardStore } = await import('./index');
                  const boardState = useBoardStore.getState();
                  const results = await Promise.allSettled([
                    boardState.fetchCloudProjects(),
                    boardState.fetchCloudFolders(),
                  ]);
                  results.forEach((r) => {
                    if (r.status === 'rejected' && r.reason instanceof Error && r.reason.name === 'AbortError') {
                      logger.debug('[Auth] AbortError in prefetch (expected) - ignored');
                    } else if (r.status === 'rejected') {
                      logger.error('[Auth] Failed to prefetch projects/folders:', r.reason);
                    }
                  });
                } catch (error) {
                  logger.error('[Auth] Failed to prefetch projects/folders:', error);
                }

              }

              // Clean OAuth callback params/hash from URL after successful login
              if (hasAuthCallback && user) {
                logger.debug('[Auth] Cleaning OAuth callback from URL');
                cleanAuthCallbackUrl();
              }
            });
            logger.debug('[Auth] Listener active');
          } else {
            logger.debug('[Auth] Listener already active');
          }

          // ✅ After OAuth callback, check session with a short delay as fallback.
          // The onAuthStateChange listener should fire automatically when Supabase
          // processes the token, but in some cases (slow network, race conditions)
          // the listener may fire after the UI has already rendered as logged-out.
          if (hasAuthCallback) {
            // Try to apply the session that Supabase establishes from the OAuth
            // code exchange. The onAuthStateChange listener should fire SIGNED_IN
            // automatically, but on slow/production networks the exchange can
            // finish after the UI has rendered as logged-out (and the listener
            // event can be missed). We poll a few times with backoff so the UI
            // updates on its own, instead of depending on a single 1.5s timeout
            // or a manual page refresh.
            const applyOAuthSession = async (): Promise<boolean> => {
              if (_get().isAuthenticated) return true;
              try {
                const { data: { session } } = await supabase!.auth.getSession();
                if (!session?.user) return false;

                const user = await getCurrentUser(session.user);
                if (!user) return false;

                set({
                  user,
                  isAuthenticated: true,
                  ...accessFromUser(user),
                  isLoading: false,
                });
                logger.debug('[Auth] OAuth fallback: session applied for', user.email);

                // Load preferences
                try {
                  const cloudPrefs = await getPreferences();
                  await applyCloudPreferences(cloudPrefs);
                  logger.debug('[Auth] Preferences loaded from cloud (fallback)');
                } catch (error) {
                  logger.error('[Auth] Failed to load preferences (fallback):', error);
                }
                return true;
              } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                  logger.debug('[Auth] AbortError in fallback (expected) - ignored');
                } else {
                  logger.error('[Auth] Fallback session check error:', error);
                }
                return false;
              }
            };

            // Backoff schedule (ms from now) covering slow production exchanges.
            const retryDelays = [400, 1000, 2000, 3500, 5000];
            retryDelays.forEach((delay) => {
              window.setTimeout(async () => {
                const applied = await applyOAuthSession();
                // Never remove ?code=... before Supabase finishes PKCE exchange.
                // The code is single-use; cleaning it early can break Google login.
                if (applied && hasAuthCallbackInUrl()) {
                  cleanAuthCallbackUrl();
                }
              }, delay);
            });
          }

          // ✅ Check for existing session ONLY if NOT OAuth callback
          // (OAuth callback will be processed by Supabase and trigger onAuthStateChange)
          if (!hasAuthCallback) {
            logger.debug('[Auth] Checking for existing session...');
            try {
              const { data: { session } } = await supabase.auth.getSession();
              
              if (session?.user) {
                logger.debug('[Auth] Existing session found:', session.user.email);
                const user = await getCurrentUser();
                if (user) {
                  logger.debug('[Auth] User profile loaded:', user.email);
                  set({
                    user,
                    isAuthenticated: true,
                    ...accessFromUser(user),
                  });

                  // Load preferences for existing session
                  try {
                    const cloudPrefs = await getPreferences();
                    await applyCloudPreferences(cloudPrefs);
                    logger.debug('[Auth] Preferences loaded from cloud');
                  } catch (error) {
                    logger.error('[Auth] Failed to load preferences:', error);
                  }
                }
              } else {
                logger.debug('[Auth] No existing session');
              }
            } catch (error) {
              // Ignore AbortError - it's expected during OAuth processing
              if (error instanceof Error && error.name === 'AbortError') {
                logger.debug('[Auth] AbortError (expected during OAuth) - ignored');
              } else {
                logger.error('[Auth] Session check error:', error);
              }
            }
          } else {
            logger.debug('[Auth] Skipping session check - OAuth callback will be handled by listener');
          }
          
        } catch (error) {
          logger.error('[Auth] Initialization error:', error);
          set({
            error: 'auth.errorInitFailed',
          });
        }
      },

      // Sign in with email/password
      signIn: async (email: string, password: string) => {
        if (!isSupabaseEnabled()) {
          set({ error: 'auth.errorOfflineMode' });
          return;
        }

        set({
          isLoading: true,
          error: null,
          authFlow: {
            status: 'emailSubmitting',
            method: 'email',
            surface: null,
            startedAt: Date.now(),
            error: null,
            retryable: false,
          },
        });

        try {
          const authResponse = await supabaseSignIn(email, password);
          
          // Set basic user state from signIn response immediately.
          // The onAuthStateChange listener will fill in the full profile
          // (preferences, projects, folders) asynchronously — avoid calling
          // getCurrentUser() here to prevent racing with the listener on
          // supabase-js internal locks (locks.js AbortError).
          const authUser = authResponse?.user ?? null;
          set({
            user: authUser ? {
              id: authUser.id,
              email: authUser.email ?? '',
              subscription_tier: 'free',
            } : null,
            isAuthenticated: !!authUser,
            isLoading: false,
            authFlow: { ...idleAuthFlow, status: authUser ? 'done' : 'idle' },
          });
          if (authUser) track(EVENTS.AUTH_SUCCESS, { method: 'email' });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed';

          // Supabase returns a generic error for unconfirmed emails.
          // Detect the pattern and forward a clearer sentinel key so the
          // UI can show "check your inbox / resend" instead of a raw error.
          const msgLower = message.toLowerCase();
          const isUnconfirmed =
            msgLower.includes('email_not_confirmed') ||
            msgLower.includes('email not confirmed') ||
            msgLower.includes('confirm') ||
            message === 'Sign in failed';

          set({
            isLoading: false,
            error: isUnconfirmed ? 'auth.errorEmailNotConfirmed' : message,
            authFlow: {
              status: 'error',
              method: 'email',
              surface: null,
              startedAt: null,
              error: isUnconfirmed ? 'auth.errorEmailNotConfirmed' : message,
              retryable: true,
            },
          });
          throw error;
        }
      },

      // Sign up with email/password
      signUp: async (email: string, password: string, fullName?: string) => {
        if (!isSupabaseEnabled()) {
          set({ error: 'auth.errorOfflineMode' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          await supabaseSignUp(email, password, fullName);
          set({
            isLoading: false,
            error: null,
          });
          track(EVENTS.SIGNUP, { method: 'email' });
          // Note: User needs to verify email, so don't set authenticated yet
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign up failed';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      // Sign in with Google
      signInWithGoogle: async () => {
        if (!isSupabaseEnabled()) {
          set({ error: 'auth.errorOfflineMode' });
          return;
        }

        let popup: Window | null = null;
        set({
          isLoading: false,
          isOAuthInProgress: true,
          error: null,
          authFlow: {
            status: 'oauthOpening',
            method: 'google',
            surface: null,
            startedAt: Date.now(),
            error: null,
            retryable: false,
          },
        });

        try {
          logger.debug('[Auth] Starting Google sign in...');

          // H4: Save current work to localStorage BEFORE OAuth redirect
          // This prevents loss of unsaved work during the redirect flow
          try {
            const { useBoardStore } = await import('./index');
            useBoardStore.getState().saveDocument();
            logger.debug('[Auth] Board state saved before OAuth redirect');
          } catch (e) {
            logger.error('[Auth] Failed to save before redirect:', e);
          }

          const finishGoogleLogin = async (authUser: SupabaseAuthUser) => {
            const user = await getCurrentUser(authUser);
            if (!user) {
              throw new Error('Google login finished, but user profile could not be loaded');
            }

            set({
              user,
              isAuthenticated: true,
              ...accessFromUser(user),
              isLoading: false,
              isOAuthInProgress: false,
              error: null,
              authFlow: {
                status: 'sessionReady',
                method: 'google',
                surface: _get().authFlow.surface,
                startedAt: _get().authFlow.startedAt,
                error: null,
                retryable: false,
              },
            });
            logger.debug('[Auth] Google login applied for', user.email);
            track(EVENTS.AUTH_SUCCESS, { method: 'google' });

            void (async () => {
              try {
                const cloudPrefs = await getPreferences();
                // B1.4 + B1.5: load cloud prefs (or push local if first login)
                await applyCloudPreferences(cloudPrefs);
              } catch (error) {
                logger.error('[Auth] Failed to load/preferences after Google login:', error);
              }

              try {
                const { useBoardStore } = await import('./index');
                const boardState = useBoardStore.getState();
                const results = await Promise.allSettled([
                  boardState.fetchCloudProjects(),
                  boardState.fetchCloudFolders(),
                ]);
                results.forEach((result) => {
                  if (result.status === 'rejected') {
                    logger.error('[Auth] Failed to prefetch after Google login:', result.reason);
                  }
                });
              } catch (error) {
                logger.error('[Auth] Failed to prefetch after Google login:', error);
              }
            })();
          };

          const surface = resolveGoogleOAuthSurface();
          set((state) => ({
            authFlow: {
              ...state.authFlow,
              status: 'oauthWaitingForProvider',
              surface,
            },
          }));
          if (surface === 'web-redirect') {
            await supabaseSignInWithGoogle({
              redirectTo: `${window.location.origin}/auth/callback`,
              skipBrowserRedirect: false,
            });
            return;
          }

          if (surface === 'desktop-deeplink') {
            const result = await supabaseSignInWithGoogle({
              redirectTo: getDesktopAuthRedirectTo(),
              skipBrowserRedirect: true,
            });

            if (!result?.url) {
              throw new Error('Google login URL was not returned by Supabase');
            }

            const code = await waitForDesktopOAuthCode(result.url);
            set((state) => ({
              authFlow: {
                ...state.authFlow,
                status: 'oauthCallbackReceived',
              },
            }));
            if (!supabase) throw new Error('Supabase not configured');

            const { data, error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) throw error;
            if (!data.session?.user) {
              throw new Error('Google login finished, but no Supabase session was found');
            }

            await finishGoogleLogin(data.session.user);
            return;
          }

          try {
            popup = openOAuthPopup();
          } catch {
            logger.warn('[Auth] Google popup unavailable - falling back to full-page redirect');
            await supabaseSignInWithGoogle({
              redirectTo: `${window.location.origin}/auth/callback`,
              skipBrowserRedirect: false,
            });
            return;
          }
          
          const result = await supabaseSignInWithGoogle({
            redirectTo: `${window.location.origin}${AUTH_POPUP_CALLBACK_PATH}`,
            skipBrowserRedirect: true,
          });

          if (!result?.url) {
            throw new Error('Google login URL was not returned by Supabase');
          }

          // The static callback page posts the PKCE authorization code back
          // to this window; the popup itself never touches Supabase.
          const codePromise = waitForOAuthPopupCode(popup);
          popup.location.href = result.url;
          const code = await codePromise;

          set((state) => ({
            authFlow: {
              ...state.authFlow,
              status: 'oauthCallbackReceived',
            },
          }));

          // Exchange the code HERE, in the main window — single Supabase
          // client, no cross-window navigator.locks contention, no polling.
          if (!supabase) throw new Error('Supabase not configured');
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          if (!data.session?.user) {
            throw new Error('Google login finished, but no Supabase session was found');
          }

          await finishGoogleLogin(data.session.user);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Google sign in failed';
          logger.error('[Auth] Google sign in error:', error);
          try {
            popup?.close();
          } catch {
            // no-op
          }
          set((state) => ({
            isLoading: false,
            isOAuthInProgress: false,
            error: message,
            authFlow: {
              status: 'error',
              method: 'google',
              surface: state.authFlow.surface,
              startedAt: state.authFlow.startedAt,
              error: message,
              retryable: true,
            },
          }));
          throw error;
        }
      },

      // Sign out
      signOut: async () => {
        // DEV-ONLY: mock sessions have no real Supabase session to clear.
        if (_get().isMockUser) {
          _get().devLogout();
          return;
        }

        set({ isLoading: true, error: null });

        try {
          await supabaseSignOut();
          set({
            user: null,
            isAuthenticated: false,
            isPro: false,
            isTeam: false,
            teamId: null,
            isLoading: false,
            isOAuthInProgress: false,
            authFlow: idleAuthFlow,
          });
          logger.debug('[Auth] Successfully signed out');

          // PR-B1: Post-Logout Data Cleanup
          // Clear board state and remove any previous user data
          try {
            const { useBoardStore } = await import('./index');
            const boardState = useBoardStore.getState();
            
            // 1. Reset board to blank document (also resets cloudProjectId to null)
            boardState.newDocument();
            
            // 2. Clear persisted board document from localStorage
            localStorage.removeItem('tmc-studio-board');
            
            // 3. Clear any active autosave timer
            boardState.clearAutoSaveTimer();
            
            logger.debug('[Auth] Board state cleaned up after logout');
          } catch (error) {
            logger.error('[Auth] Failed to clean up board state:', error);
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign out failed';
          logger.error('[Auth] Sign out error:', error);
          set({ isLoading: false, error: message });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Send reset password email
      sendResetLink: async (email: string) => {
        if (!isSupabaseEnabled()) {
          set({ error: 'auth.errorOfflineMode' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          await supabaseResetPasswordForEmail(email);
          set({ isLoading: false });
          // Don't clear the form state — let the modal show success message
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to send reset link';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      // Resend confirmation email
      resendConfirmation: async (email: string) => {
        if (!isSupabaseEnabled()) {
          set({ error: 'auth.errorOfflineMode' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          await supabaseResendConfirmation(email);
          set({ isLoading: false, error: null });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to resend confirmation email';
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      // ===== DEV-ONLY TEST AUTH =====
      // Lets devs flip between guest/free/pro/team instantly without
      // going through Google OAuth. Safe to delete this block (and the
      // matching UI buttons) once real auth testing is no longer needed -
      // it does not touch Supabase and has no effect on real users.
      devLogin: (tier) => {
        if (tier === 'guest') {
          set({
            user: null,
            isAuthenticated: false,
            isPro: false,
            isTeam: false,
            teamId: null,
            isMockUser: true,
            isOAuthInProgress: false,
            error: null,
            authFlow: idleAuthFlow,
          });
          setDevCloudUser(null);
          logger.debug('[Auth][DEV] Logged in as guest (mock)');
          return;
        }

        const mockUser: User = {
          id: `dev-${tier}-user`,
          email: `dev-${tier}@tmcstudio.test`,
          full_name: `Test ${tier.charAt(0).toUpperCase()}${tier.slice(1)} User`,
          subscription_tier: tier,
        };

        set({
          user: mockUser,
          isAuthenticated: true,
          isPro: tier === 'pro' || tier === 'team',
          isTeam: tier === 'team',
          teamId: tier === 'team' ? 'dev-team-id' : null,
          isMockUser: true,
          isOAuthInProgress: false,
          error: null,
          authFlow: idleAuthFlow,
        });
        setDevCloudUser(mockUser.id);
        logger.debug(`[Auth][DEV] Logged in as mock ${tier} user`);

        // Prefetch this mock user's devCloud projects/folders so limit counts
        // are correct right away (mirrors the real sign-in prefetch above).
        (async () => {
          try {
            const { useBoardStore } = await import('./index');
            const boardState = useBoardStore.getState();
            await Promise.all([
              boardState.fetchCloudProjects(),
              boardState.fetchCloudFolders(),
            ]);
          } catch (error) {
            logger.error('[Auth][DEV] Failed to prefetch after devLogin:', error);
          }
        })();
      },

      devLogout: () => {
        set({
          user: null,
          isAuthenticated: false,
          isPro: false,
          isTeam: false,
          teamId: null,
          isMockUser: false,
          isLoading: false,
          isOAuthInProgress: false,
          error: null,
          authFlow: idleAuthFlow,
        });
        setDevCloudUser(null);
        logger.debug('[Auth][DEV] Mock session cleared');

        // Mirror the real signOut's local cleanup so board state doesn't
        // leak between test sessions.
        (async () => {
          try {
            const { useBoardStore } = await import('./index');
            const boardState = useBoardStore.getState();
            boardState.newDocument();
            localStorage.removeItem('tmc-studio-board');
            boardState.clearAutoSaveTimer();
          } catch (error) {
            logger.error('[Auth][DEV] Failed to clean up board state:', error);
          }
        })();
      },

      devClearData: () => {
        clearDevCloudData();
        logger.debug('[Auth][DEV] Cleared devCloud data for current mock user');

        (async () => {
          try {
            const { useBoardStore } = await import('./index');
            const boardState = useBoardStore.getState();
            boardState.newDocument();
            localStorage.removeItem('tmc-studio-board');
            await boardState.fetchCloudProjects();
            await boardState.fetchCloudFolders();
          } catch (error) {
            logger.error('[Auth][DEV] Failed to refresh after clearing dev data:', error);
          }
        })();
      },

      devClearAllTiers: () => {
        clearAllDevCloudData();
        logger.debug('[Auth][DEV] Cleared devCloud data for ALL test tiers');

        (async () => {
          try {
            const { useBoardStore } = await import('./index');
            const boardState = useBoardStore.getState();
            boardState.newDocument();
            localStorage.removeItem('tmc-studio-board');
            await boardState.fetchCloudProjects();
            await boardState.fetchCloudFolders();
          } catch (error) {
            logger.error('[Auth][DEV] Failed to refresh after clearing all dev data:', error);
          }
        })();
      },
    }),
    {
      name: 'tmc-auth',
      partialize: (state) => ({
        // Only persist non-sensitive data
        isInitialized: state.isInitialized,
        // DEV-ONLY: persist mock test sessions across reloads so the
        // "Test login" state survives a page refresh during testing.
        isMockUser: state.isMockUser,
        user: state.isMockUser ? state.user : undefined,
        isAuthenticated: state.isMockUser ? state.isAuthenticated : undefined,
        isPro: state.isMockUser ? state.isPro : undefined,
        isTeam: state.isMockUser ? state.isTeam : undefined,
      }),
    }
  )
);

// Auto-initialize on import - ALWAYS run, don't check isInitialized
if (typeof window !== 'undefined') {
  // Defer initialization slightly to let Supabase client process any OAuth hash
  setTimeout(() => {
    logger.debug('[Auth] Auto-init triggered');
    const { initialize } = useAuthStore.getState();
    initialize();
  }, 100);
}
